import { makeAssistantToolUI } from '@assistant-ui/react'
import { createContext, useContext } from 'react'
import { ClarificationCard } from '@/components/agent/ClarificationCard'
import { ClarificationSummary } from '@/components/schema/agent/ClarificationSummary'
import type {
  SchemaClarification,
  SchemaClarificationAnswerBody,
} from '@/services/api/schemaSessions'

/** The run's latest stop and how to answer it; the workbench chat provides it to the thread. */
export interface SchemaClarificationState {
  /** The latest stop the run made: open while `waiting`, answered afterwards. */
  clarification: SchemaClarification | null
  waiting: boolean
  busy: boolean
  answer: (answers: SchemaClarificationAnswerBody['answers']) => Promise<void>
}

export const SchemaClarificationContext = createContext<SchemaClarificationState | null>(null)

interface ClarificationArgs {
  context?: string
  questions?: { question?: string; options?: string[] }[]
}

// What became of the stop rides the tool message's artifact as `{ outcome, answers }`
// (CLARIFICATION_ANSWERED / CLARIFICATION_REFUSED in schema_builder/tools.py).
const REFUSED = 'refused'

/** The question texts the agent sent, in order; the model's arguments are not trusted to be well formed. */
export function askedQuestions(args: ClarificationArgs | undefined): string[] {
  const questions: unknown[] = Array.isArray(args?.questions) ? args.questions : []
  return questions.flatMap(item =>
    typeof item === 'object' &&
    item !== null &&
    'question' in item &&
    typeof item.question === 'string' &&
    item.question.trim()
      ? [item.question.trim()]
      : []
  )
}

function artifactField(artifact: unknown, field: 'outcome' | 'answers'): unknown {
  return typeof artifact === 'object' && artifact !== null && field in artifact
    ? (artifact as Record<string, unknown>)[field]
    : undefined
}

/** Answers by question, from the artifact or from the session's copy of the stop. */
function answersByQuestion(
  artifact: unknown,
  stop: SchemaClarification | null
): Map<string, string> {
  const reported = artifactField(artifact, 'answers')
  const rows: unknown[] = Array.isArray(reported) ? reported : (stop?.answers ?? [])
  return new Map(
    rows.flatMap(row =>
      typeof row === 'object' &&
      row !== null &&
      'question' in row &&
      'answer' in row &&
      typeof row.question === 'string' &&
      typeof row.answer === 'string'
        ? [[row.question.trim(), row.answer] as const]
        : []
    )
  )
}

function isSameStop(stop: SchemaClarification, asked: string[]): boolean {
  const questions = stop.questions.map(item => item.question.trim())
  return questions.length === asked.length && questions.every((q, index) => q === asked[index])
}

export function SchemaClarificationToolCard({
  args,
  result,
  artifact,
}: {
  args: ClarificationArgs | undefined
  result: unknown
  artifact: unknown
}) {
  const state = useContext(SchemaClarificationContext)
  const asked = askedQuestions(args)
  // A refused stop never reached the analyst; the agent asks again in a later message.
  if (artifactField(artifact, 'outcome') === REFUSED || asked.length === 0) return null
  const stop =
    state?.clarification && isSameStop(state.clarification, asked) ? state.clarification : null
  if (stop && state?.waiting && result === undefined)
    return (
      <div className="my-2">
        <ClarificationCard clarification={stop} busy={state.busy} onSubmit={state.answer} />
      </div>
    )
  const answers = answersByQuestion(artifact, stop)
  return (
    <ClarificationSummary
      context={typeof args?.context === 'string' ? args.context : null}
      items={asked.map(question => ({ question, answer: answers.get(question) ?? null }))}
    />
  )
}

/** The agent's questions inside the workbench conversation: answerable while the run waits, a record afterwards. */
export const SchemaClarificationToolUI = makeAssistantToolUI<ClarificationArgs, string>({
  toolName: 'request_clarification',
  render: ({ args, result, artifact }) => (
    <SchemaClarificationToolCard args={args} result={result} artifact={artifact} />
  ),
})
